import { formatDate } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { Tag } from '@models/common/tag';
import { IndividualRecord } from '@models/individuals/Individual-record';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CommonModalFormComponent } from "@shared/components/commons/common-modal-form/common-modal-form.component";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { AgEntityTagsComponent } from '@shared/components/grid/ag-cells/common/ag-entity-tags/ag-entity-tags.component';
import {
  AgIndividualNameComponent
} from '@shared/components/grid/ag-cells/common/ag-individual-name/ag-individual-name.component';
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { CountryPipe } from '@shared/pipes/countryPipe';
import { DinPipe } from '@shared/pipes/din.pipe';
import { PhonePipe } from '@shared/pipes/phonePipe';
import { IndividualsApiService } from "@shared/services/individuals-api.service";
import { ColDef, GridApi, type RowSelectionOptions, ValueFormatterParams } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { EMPTY } from 'rxjs';

@Component({
  selector: 'app-individual-merge-modal',
  standalone: true,
  imports: [
    CommonModalFormComponent,
    ListGridClientSideComponent,
    DisclaimerComponent
  ],
  templateUrl: 'individual-merge-modal.component.html',
  styleUrl: 'individual-merge-modal.component.scss'
})
export class IndividualMergeModalComponent implements OnInit {
  private individualApiService = inject(IndividualsApiService);
  private activeModal = inject(NgbActiveModal);
  private toastr = inject(ToastrService);

  @Input() individualRecords!: IndividualRecord[];

  selectedIndividualRecords: IndividualRecord[] = [];
  isMergeLoading = false;
  moreThenOneEntitySelected = false;

  ngOnInit(): void {
    this.setColDef();
  }

  merge(): void {
    this.isMergeLoading = true;
    const mainIndividual = this.selectedIndividualRecords[0];
    const individualsIdToMerge = this.individualRecords
      .filter(i => i !== mainIndividual)
      .map(individual => individual.individualId);

    this.individualApiService.merge(mainIndividual.individualId, individualsIdToMerge)
      .subscribe({
        next: () => {
          this.toastr.success("Individuals merged successfully");

        },
        error: () => {
          this.toastr.error("Error while merging individuals");
          this.isMergeLoading = false;
          return EMPTY;
        },
        complete: () => {
          this.activeModal.close();
        }
      });
  }

  close(): void {
    this.activeModal.dismiss();
  }

  // =====  Grid  =====

  readonly rowSelection: RowSelectionOptions<any> | 'single' | 'multiple' | undefined = {
    mode: 'singleRow',
    enableClickSelection: false,
    checkboxes: true,
    checkboxLocation: 'selectionColumn',
    masterSelects: 'self'
  };

  colDefs: ColDef[] = [];
  gridApi!: GridApi;

  selectMainIndividual(externalEntities: IndividualRecord[]): void {
    this.selectedIndividualRecords = externalEntities;
    this.moreThenOneEntitySelected = this.selectedIndividualRecords.length > 1;
  }

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
  }

  setColDef() {
    this.colDefs = [
      {
        headerName: 'Name',
        field: 'fullName',
        width: 150,
        flex: 1,
        editable: false,
        sort: 'asc',
        suppressMovable: true,
        cellRenderer: AgIndividualNameComponent,
      },
      {
        headerName: 'Email address',
        field: 'email',
        width: 150,
        flex: 1,
      },
      {
        headerName: 'Mobile number',
        field: 'phone',
        width: 150,
        valueFormatter: (params: ValueFormatterParams<IndividualRecord, string>) => PhonePipe.formatValue(params.data?.phone),
        flex: 1,
      },
      {
        headerName: 'Director ID',
        field: 'din',
        width: 150,
        valueFormatter: (params: ValueFormatterParams<IndividualRecord, string>) => DinPipe.formatValue(params.data?.din),
        flex: 1,
      },
      {
        headerName: 'Date of Birth',
        field: 'dob',
        width: 90,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<IndividualRecord, Date>) => {
          return params.value ? formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US') : '-';
        },
      },
      {
        headerName: 'Place of Birth',
        field: 'birthCity',
        width: 90,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<IndividualRecord>) => CountryPipe.formatPlaceOfBirth(params.data?.birthCity, params.data?.birthCountry, '-'),
        hide: true,
      },
      {
        headerName: 'Tags',
        field: 'tags',
        width: 150,
        flex: 1,
        sortable: false,
        cellRenderer: AgEntityTagsComponent,
        valueFormatter: (params: { value: Tag }) => params.value?.name ?? '',
      }];
  }
}
